// frontend/src/components/chat/ChatSearch.jsx
// PHASE 5: Search through chat message history
// Can search all conversations or within current conversation

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useChat } from "../../contexts/ChatContext";

const ChatSearch = ({ onClose, onNavigate }) => {
  const { searchMessages, activeConversation } = useChat();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [searchInCurrent, setSearchInCurrent] = useState(false);
  const [error, setError] = useState(null);

  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const LIMIT = 20;

  // Focus input on mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Perform search
  const performSearch = useCallback(
    async (searchQuery, searchOffset = 0, append = false) => {
      if (!searchQuery.trim()) {
        setResults([]);
        setTotal(0);
        setHasMore(false);
        return;
      }

      setIsSearching(true);
      setError(null);

      try {
        const options = {
          limit: LIMIT,
          offset: searchOffset,
        };

        // If searching in current conversation, add conversation_id
        if (searchInCurrent && activeConversation?.conversation_id) {
          options.conversationId = activeConversation.conversation_id;
        }

        const data = await searchMessages(searchQuery, options);

        if (append) {
          setResults((prev) => [...prev, ...data.messages]);
        } else {
          setResults(data.messages);
        }
        setTotal(data.total);
        setHasMore(data.hasMore);
        setOffset(searchOffset + data.messages.length);
      } catch (err) {
        console.error("Search failed:", err);
        setError("Search failed. Please try again.");
        if (!append) {
          setResults([]);
          setTotal(0);
          setHasMore(false);
        }
      } finally {
        setIsSearching(false);
      }
    },
    [searchMessages, searchInCurrent, activeConversation]
  );

  // Debounced search on query change
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.trim().length >= 2) {
      debounceRef.current = setTimeout(() => {
        performSearch(query, 0, false);
      }, 300);
    } else {
      setResults([]);
      setTotal(0);
      setHasMore(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  // Re-search when toggle changes
  useEffect(() => {
    if (query.trim().length >= 2) {
      performSearch(query, 0, false);
    }
  }, [searchInCurrent]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load more results
  const loadMore = () => {
    if (hasMore && !isSearching) {
      performSearch(query, offset, true);
    }
  };

  // Handle result click - navigate to conversation and message
  const handleResultClick = (result) => {
    if (onNavigate) {
      onNavigate(result.conversation_id, result.message_id);
    }
    if (onClose) {
      onClose();
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return date.toLocaleDateString("en-US", { weekday: "short" });
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    }
  };

  // Highlight matching text
  const highlightMatch = (text, searchTerm) => {
    if (!text || !searchTerm) return text;

    const regex = new RegExp(
      `(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi"
    );
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} className="search-highlight">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Truncate content around match
  const truncateContent = (content, searchTerm, maxLength = 100) => {
    if (!content) return "";

    const lowerContent = content.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase();
    const matchIndex = lowerContent.indexOf(lowerSearch);

    if (matchIndex === -1 || content.length <= maxLength) {
      return content.length > maxLength
        ? content.slice(0, maxLength) + "..."
        : content;
    }

    // Show content around the match
    const start = Math.max(0, matchIndex - 30);
    const end = Math.min(content.length, matchIndex + searchTerm.length + 70);

    let result = "";
    if (start > 0) result += "...";
    result += content.slice(start, end);
    if (end < content.length) result += "...";

    return result;
  };

  return (
    <div className="chat-search-panel">
      {/* Search Header */}
      <div className="chat-search-header">
        <div className="search-input-container">
          <i className="bi bi-search search-icon"></i>
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Search messages..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              className="search-clear-btn"
              onClick={() => setQuery("")}
              title="Clear search"
            >
              <i className="bi bi-x"></i>
            </button>
          )}
        </div>
        <button
          className="search-close-btn"
          onClick={onClose}
          title="Close search"
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>

      {/* Search Options */}
      {activeConversation && (
        <div className="chat-search-options">
          <label className="search-option-toggle">
            <input
              type="checkbox"
              checked={searchInCurrent}
              onChange={(e) => setSearchInCurrent(e.target.checked)}
            />
            <span>Search only in this conversation</span>
          </label>
        </div>
      )}

      {/* Search Results */}
      <div className="chat-search-results">
        {/* Loading indicator */}
        {isSearching && results.length === 0 && (
          <div className="search-loading">
            <span className="spinner-border spinner-border-sm me-2"></span>
            Searching...
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="search-error">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {/* No results */}
        {!isSearching &&
          query.trim().length >= 2 &&
          results.length === 0 &&
          !error && (
            <div className="search-no-results">
              <i className="bi bi-inbox"></i>
              <div>No messages found</div>
              <small>Try different search terms</small>
            </div>
          )}

        {/* Results count */}
        {results.length > 0 && (
          <div className="search-results-count">
            Found {total} message{total !== 1 ? "s" : ""}
          </div>
        )}

        {/* Results list */}
        {results.map((result) => (
          <div
            key={result.message_id}
            className="search-result-item"
            onClick={() => handleResultClick(result)}
          >
            <div className="search-result-header">
              <span className="search-result-sender">
                {result.sender_name || "Unknown"}
              </span>
              <span className="search-result-conversation">
                in chat with {result.other_participant_name || "..."}
              </span>
              <span className="search-result-time">
                {formatDate(result.created_at)}
              </span>
            </div>
            <div className="search-result-content">
              {highlightMatch(truncateContent(result.content, query), query)}
            </div>
            {result.has_attachment && (
              <div className="search-result-attachment">
                <i className="bi bi-paperclip me-1"></i>
                Attachment
              </div>
            )}
          </div>
        ))}

        {/* Load more button */}
        {hasMore && (
          <button
            className="search-load-more"
            onClick={loadMore}
            disabled={isSearching}
          >
            {isSearching ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Loading...
              </>
            ) : (
              "Load more results"
            )}
          </button>
        )}

        {/* Hint for short queries */}
        {query.trim().length > 0 && query.trim().length < 2 && (
          <div className="search-hint">
            Type at least 2 characters to search
          </div>
        )}

        {/* Empty state */}
        {!query && (
          <div className="search-empty-state">
            <i className="bi bi-search"></i>
            <div>Search your messages</div>
            <small>Find messages across all conversations</small>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatSearch;
